import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '@/lib/store/authStore';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { AlertCircle, Loader2, ChevronLeft, Mail } from 'lucide-react';
import { cpInitiateLineLogin, storeLineState, cpGoogleOneTap } from '@/lib/api/controlPlaneClient';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';
import { TuesdayOSLogo } from '@/components/icons/TuesdayOSLogo';
import { AnimatePresence, motion } from 'framer-motion';
import posthog from 'posthog-js';
import { LoginCarousel } from './components/LoginCarousel';

type LoginView = 'methods' | 'email';

const BRAND_SUFFIXES = ['os', 'business', 'จัดการธุรกิจ', 'chat', 'รวมแชท', 'pos', 'ขายหน้าร้าน', 'rewards', 'สะสมแต้ม'];

/** Detect LINE's in-app browser — Google Sign-In causes interference there. */
const isLineBrowser = /\bLine\//i.test(navigator.userAgent);

export function LoginPage() {
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const getDefaultFrappeUrl = () => {
    if (import.meta.env.VITE_FRAPPE_API_URL) {
      return import.meta.env.VITE_FRAPPE_API_URL;
    }
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return window.location.origin;
    }
    return 'http://localhost:8000';
  };

  const [frappeUrl, setFrappeUrl] = useState(getDefaultFrappeUrl());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [view, setView] = useState<LoginView>('methods');
  const [suffixIndex, setSuffixIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSuffixIndex((i) => (i + 1) % BRAND_SUFFIXES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const suffix = BRAND_SUFFIXES[suffixIndex];

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const authMode = useAuthStore((state) => state.authMode);
  const login = useAuthStore((state) => state.login);
  const loginControlPlane = useAuthStore((state) => state.loginViaControlPlane);
  const loginWithCPResponse = useAuthStore((state) => state.loginWithCPResponse);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const validatedRedirect = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
    ? redirectParam
    : null;
  const intentParam = searchParams.get('intent') || undefined;

  // Google One Tap handler
  const handleGoogleCredential = async (credential: string, selectBy: string) => {
    setGoogleLoading(true);
    setError(null);

    if (posthog.__loaded) {
      posthog.capture('login_method_selected', { method: 'google', select_by: selectBy });
    }

    try {
      const response = await cpGoogleOneTap({ credential });
      loginWithCPResponse(response);

      if (posthog.__loaded) {
        posthog.capture('login_completed', { method: 'google' });
      }

      navigate(validatedRedirect || '/');
    } catch (err: any) {
      setError(err.message || t.googleLoginFailed);
    } finally {
      setGoogleLoading(false);
    }
  };

  const googleBtnRef = useRef<HTMLDivElement>(null);

  const { isReady, renderButton } = useGoogleOneTap({
    onSuccess: handleGoogleCredential,
    onError: (msg) => setError(msg),
    autoPrompt: view === 'methods' && authMode === 'control_plane' && !isLineBrowser,
    disabled: isAuthenticated || authMode !== 'control_plane' || isLineBrowser,
  });

  // Render the real GIS button as an invisible overlay (preserves user gesture for popup OAuth)
  const googleLoginBtnCallback = useCallback(
    (node: HTMLDivElement | null) => {
      googleBtnRef.current = node;
      if (node && isReady) {
        renderButton(node);
      }
    },
    [isReady, renderButton],
  );

  useEffect(() => {
    if (isReady && googleBtnRef.current) {
      renderButton(googleBtnRef.current);
    }
  }, [isReady, renderButton]);

  useEffect(() => {
    if (hasHydrated && isAuthenticated) {
      navigate(validatedRedirect || '/', { replace: true });
    }
  }, [hasHydrated, isAuthenticated, navigate, validatedRedirect]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (authMode === 'control_plane') {
        await loginControlPlane(username, password);
      } else {
        const trimmedUrl = frappeUrl.trim();
        if (!trimmedUrl) {
          throw new Error(t.errorFrappeUrlRequired);
        }

        let normalizedUrl = trimmedUrl;
        if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
          if (trimmedUrl.includes('localhost') || trimmedUrl.includes('127.0.0.1') || /^\d+\.\d+\.\d+\.\d+/.test(trimmedUrl)) {
            normalizedUrl = `http://${trimmedUrl}`;
          } else {
            throw new Error(t.errorInvalidUrl);
          }
        }
        normalizedUrl = normalizedUrl.replace(/127\.0\.0\.1/g, 'localhost');

        await login(normalizedUrl, username, password);
      }

      // Track login method for Google Ads funnel
      if (posthog.__loaded) {
        posthog.capture('login_completed', { method: 'email' });
      }

      navigate(validatedRedirect || '/');
    } catch (err: any) {
      setError(err.message || t.errorLoginFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleLineLogin = async () => {
    setLineLoading(true);
    setError(null);

    // Track login method for Google Ads funnel
    if (posthog.__loaded) {
      posthog.capture('login_method_selected', { method: 'line' });
    }

    try {
      const { auth_url, state } = await cpInitiateLineLogin('/', intentParam);
      storeLineState(state, intentParam);
      window.location.href = auth_url;
    } catch (err: any) {
      setError(err.message || t.lineLoginFailed);
      setLineLoading(false);
    }
  };

  const handleEmailMethodClick = () => {
    setView('email');
    setError(null);

    if (posthog.__loaded) {
      posthog.capture('login_method_selected', { method: 'email' });
    }
  };

  // Direct mode (non-control_plane) always shows email form
  const isDirectMode = authMode !== 'control_plane';
  const anyLoading = loading || lineLoading || googleLoading;

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      {/* Back button when in email form view */}
      <AnimatePresence>
        {view === 'email' && !isDirectMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="safe-top px-6 pt-4"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setView('methods'); setError(null); }}
              className="p-0 h-auto hover:bg-transparent text-muted-foreground"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              {t.back}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer — pushes carousel to center */}
      <div className="flex-1" />

      {/* Center carousel */}
      <div className="py-4">
        <LoginCarousel />
      </div>

      {/* Spacer — pushes bottom content down */}
      <div className="flex-1" />

      {/* Bottom section — logo, marketing text, CTAs all together at bottom */}
      <div className="px-6 pb-8 md:pb-12">
        <div className="w-full max-w-sm md:mx-auto">
          {/* Logo + Brand */}
          <div className="flex items-center gap-2.5 mb-4 md:justify-center">
            <motion.div
              initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              whileHover={{ rotate: 360, transition: { duration: 0.8, ease: [0.25, 0.4, 0.25, 1] } }}
              transition={{ duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }}
            >
              <TuesdayOSLogo className="w-9 h-9" />
            </motion.div>
            <motion.span
              className="leading-none tracking-[-0.03em] text-foreground"
              style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: 22, fontWeight: 500 }}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
            >
              <span className="relative inline-flex items-baseline">tuesday<AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={suffix}
                  className="text-[#0051FD]"
                  style={{
                    fontFamily: /[ก-๙]/.test(suffix) ? "'IBM Plex Sans Thai', system-ui, sans-serif" : undefined,
                    fontSize: /[ก-๙]/.test(suffix) ? '0.9em' : undefined,
                    lineHeight: 0,
                    whiteSpace: 'nowrap',
                  }}
                  initial={{ opacity: 0, y: 6, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -6, filter: 'blur(4px)' }}
                  transition={{ duration: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
                >
                  {suffix === 'os' ? suffix : `.${suffix}`}
                </motion.span>
              </AnimatePresence></span>
            </motion.span>
          </div>

          {/* Marketing text */}
          <h1 className="text-2xl font-bold text-foreground leading-tight mb-1 md:text-center">
            {view === 'email' || isDirectMode
              ? t.loginWithEmail
              : t.heroHeadline}
          </h1>
          <p className="text-muted-foreground text-sm mb-6 md:text-center">
            {view === 'email' || isDirectMode
              ? t.enterYourCredentials
              : t.heroSubline}
          </p>

          <AnimatePresence mode="wait">
            {(view === 'methods' && !isDirectMode) ? (
              /* Method selection view — three buttons: Google, LINE, Email */
              <motion.div
                key="methods"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                {/* Google — styled button with invisible GIS button overlaid.
                    Real GIS button captures user's direct click (preserves user gesture for popup OAuth).
                    Hidden in LINE's in-app browser where GIS interferes with LINE login. */}
                {!isLineBrowser && (
                  <div className="relative">
                    <GoogleSignInButton
                      onClick={() => {}}
                      loading={googleLoading}
                      disabled={anyLoading}
                      label={t.continueWithGoogle}
                    />
                    <div
                      ref={googleLoginBtnCallback}
                      className="absolute inset-0 rounded-xl [&>div]:!h-full [&_iframe]:!h-full"
                      style={{ opacity: 0 }}
                      aria-hidden="true"
                    />
                  </div>
                )}

                {/* LINE Login Button */}
                <button
                  type="button"
                  onClick={handleLineLogin}
                  disabled={anyLoading}
                  className="w-full flex items-center justify-center gap-3 rounded-xl bg-[#06C755] px-4 py-3.5 text-white font-medium text-base transition-colors hover:bg-[#05b34d] active:bg-[#04a044] disabled:opacity-50"
                >
                  {lineLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <LineIcon className="h-5 w-5" />
                  )}
                  {t.continueWithLine}
                </button>

                {/* Email Login Button */}
                <button
                  type="button"
                  onClick={handleEmailMethodClick}
                  disabled={anyLoading}
                  className="w-full flex items-center justify-center gap-3 rounded-xl border border-border bg-background px-4 py-3.5 text-foreground font-medium text-base transition-colors hover:bg-accent active:bg-accent/80 disabled:opacity-50"
                >
                  <Mail className="h-5 w-5" />
                  {t.continueWithEmail}
                </button>

                {error && (
                  <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-xl">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Sign up link */}
                <p className="pt-2 text-center text-sm text-muted-foreground">
                  {t.dontHaveAccount}{' '}
                  <Link to="/register" className="text-primary font-medium hover:underline">
                    {t.createOne}
                  </Link>
                </p>
              </motion.div>
            ) : (
              /* Email form view */
              <motion.div
                key="email"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <form onSubmit={handleSubmit} className="space-y-4">
                  {isDirectMode && (
                    <div className="space-y-2">
                      <Label htmlFor="frappe-url">{t.frappeInstanceUrl}</Label>
                      <Input
                        id="frappe-url"
                        type="text"
                        placeholder={t.frappeUrlPlaceholder}
                        value={frappeUrl}
                        onChange={(e) => setFrappeUrl(e.target.value)}
                        required
                        disabled={loading}
                        autoComplete="url"
                        className="h-12 rounded-xl !border-border !bg-background"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="username">
                      {authMode === 'control_plane' ? t.email : t.username}
                    </Label>
                    <Input
                      id="username"
                      type={authMode === 'control_plane' ? 'email' : 'text'}
                      placeholder={authMode === 'control_plane' ? t.emailPlaceholder : t.usernamePlaceholder}
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      disabled={loading}
                      autoComplete={authMode === 'control_plane' ? 'email' : 'username'}
                      autoFocus
                      className="h-12 rounded-xl !border-border !bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">{t.password}</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder={t.passwordPlaceholder}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      autoComplete="current-password"
                      className="h-12 rounded-xl !border-border !bg-background"
                    />
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-xl">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button type="submit" className="w-full h-12 rounded-xl text-base" disabled={anyLoading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t.loggingIn}
                      </>
                    ) : (
                      t.login
                    )}
                  </Button>
                </form>

                {/* Sign up link */}
                <p className="mt-4 text-center text-sm text-muted-foreground">
                  {t.dontHaveAccount}{' '}
                  <Link to="/register" className="text-primary font-medium hover:underline">
                    {t.createOne}
                  </Link>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function GoogleSignInButton({ onClick, loading, disabled, label }: {
  onClick: () => void;
  loading: boolean;
  disabled: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-3 rounded-xl border border-border bg-background px-4 py-3.5 text-foreground font-medium text-base transition-colors hover:bg-accent active:bg-accent/80 disabled:opacity-50"
    >
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <GoogleIcon className="h-5 w-5" />
      )}
      {label}
    </button>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function LineIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  );
}
